import type { NextPage } from 'next';
import Link from 'next/link';
import {
  FaChevronRight,
  FaCogs,
  FaUniversalAccess,
  FaUsers,
} from 'react-icons/fa';
import { ImSpoonKnife } from 'react-icons/im';
import { twMerge } from 'tailwind-merge';

import { useAccessibilitySettings } from '@/hooks/useAccessibilitySettings';

import Layout from '@/components/Layout';
import CallToAction from '@/components/sections/CallToAction';
import Features from '@/components/sections/Features';
import PageTitle from '@/components/sections/PageTitle';
import SEO from '@/components/utils/SEO';

const Benefits: NextPage = () => {
  const { useHighContrast } = useAccessibilitySettings();

  return (
    <Layout>
      <SEO title="Benefits" description="Benefits to restaurant owners" />

      <PageTitle
        topComponent={
          <ImSpoonKnife
            className={twMerge(
              'h-20 w-20',
              useHighContrast ? 'text-black' : 'text-primary-600'
            )}
            aria-hidden="true"
          />
        }
        title="Benefits to Restaurant Owners"
      />

      <section
        className={twMerge(
          'pt-12 pb-12 lg:pt-20 lg:pb-20',
          useHighContrast ? 'bg-contrast-yellow text-black' : 'bg-[#fbfbee]'
        )}
      >
        <div className="container">
          <h2 className={twMerge('text-2xl lg:text-3xl text-center mb-10')}>
            Ever wondered how visually impaired people read a menu?
          </h2>

          <div className="font-light space-y-6 max-w-[950px] mx-auto">
            <p>The truth is, it’s a huge problem!</p>

            <p>
              There are more than two million people with a visual impairment in
              the UK. Their conditions range from those who are totally blind
              through to people whose vision can’t be corrected by even the
              strongest of glasses.
            </p>

            <p>
              As a business how do you accommodate what equates to one in every
              30 people? To their credit, some restaurants offer Braille and
              special large print versions of their menus but did you know that
              fewer than 1% of people with visual impairment can read Braille?
            </p>

            <p>
              And what happens if you change the menu more than a couple of
              times a year?
            </p>
          </div>

          <div className="flex justify-center mt-8">
            <Link prefetch={false} href="/contact-us">
              <a
                className={twMerge(
                  'btn btn-xl inline-flex',
                  useHighContrast ? 'btn-contrast-yellow' : 'btn-primary'
                )}
              >
                Get in touch
                <FaChevronRight className="h-5 w-5 ml-4" aria-hidden="true" />
              </a>
            </Link>
          </div>
        </div>
      </section>

      <Features
        items={[
          {
            icon: (
              <FaUsers
                className={twMerge(
                  'h-14 w-14',
                  useHighContrast ? 'text-black' : 'text-primary-600'
                )}
                aria-hidden="true"
              />
            ),
            title: 'Increased Customer Base',
            description: `Make your restaurant a fully inclusive dining experience for anybody with a visual impairment. There are more than 2 million people in the UK who either can't, or struggle to read menus. Having your menu on Good Food Talks provides you with a cost effective, hastle-free and instant solution.`,
          },
          {
            icon: (
              <FaCogs
                className={twMerge(
                  'h-14 w-14',
                  useHighContrast ? 'text-black' : 'text-primary-600'
                )}
                aria-hidden="true"
              />
            ),
            title: 'No additional effort',
            description: `With our managed menu service, making your menus accessible doesn't have to be time consuming. It's a Cost-effective way to add to your bottom line`,
          },
          {
            icon: (
              <FaUniversalAccess
                className={twMerge(
                  'h-14 w-14',
                  useHighContrast ? 'text-black' : 'text-primary-600'
                )}
                aria-hidden="true"
              />
            ),
            title: 'Be socially responsible',
            description: `Every business wants their company to be socially responsible, giving all customers an equal opportunity to enjoy their products and services.`,
          },
        ]}
      />

      <CallToAction
        title="Do you want to feature your menu on GoodFoodTalks?"
        buttonHref="/contact-us"
        buttonLabel={
          <>
            {'Get started'}
            <FaChevronRight className="h-5 w-5 ml-4" aria-hidden="true" />
          </>
        }
      />
    </Layout>
  );
};

export default Benefits;
